import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { JudgeEventDashboard, JudgeRegistration, JudgeScoreStatus } from '../../types/judging';
import {
  buildScorecardReturnQuery,
  filterAndSortRegistrations,
  findFirstUnfinished,
  parseJudgeListQuery,
  storeJudgeQueue,
  vehicleLabel,
  type JudgeSortMode,
  type JudgeStatusFilter,
} from '../../utils/judgeRegistrations';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import PageLoader from '../ui/PageLoader';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

const STATUS_LABELS: Record<JudgeScoreStatus, string> = {
  not_started: 'Not started',
  draft: 'Draft',
  finalized: 'Finalized',
};

const STATUS_BADGE: Record<JudgeScoreStatus, string> = {
  not_started: 'bg-[#141414] text-gray-400 border-white/[0.08]',
  draft: 'bg-amber-950/50 text-amber-200 border-amber-800/60',
  finalized: 'bg-emerald-950/40 text-emerald-300 border-emerald-800/50',
};

const JudgeEventVehicles: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const id = Number(eventId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = parseJudgeListQuery(searchParams.toString());
  const [dashboard, setDashboard] = useState<JudgeEventDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [classId, setClassId] = useState<number | null>(initial.classId);
  const [status, setStatus] = useState<JudgeStatusFilter>(initial.status);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<JudgeSortMode>('class');

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setError(null);
        setDashboard(await apiService.getJudgeRegistrations(id));
      } catch (err: any) {
        setError(err.message || 'Failed to load vehicles');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (classId !== null) next.set('class', String(classId));
    if (status !== 'all') next.set('status', status);
    setSearchParams(next, { replace: true });
  }, [classId, status, setSearchParams]);

  const filtered = useMemo(() => {
    if (!dashboard) return [];
    return filterAndSortRegistrations(dashboard.registrations, {
      classId,
      status,
      search,
      sort,
    });
  }, [dashboard, classId, status, search, sort]);

  useEffect(() => {
    if (!id || filtered.length === 0) return;
    storeJudgeQueue(
      id,
      filtered.map((r) => r.id)
    );
  }, [id, filtered]);

  const scoreHref = (regId: number) => {
    const qs = buildScorecardReturnQuery({ classId, status });
    return `/judge/events/${id}/score/${regId}${qs}`;
  };

  const continueJudging = () => {
    const next = findFirstUnfinished(filtered);
    if (next) {
      void apiService.trackEvent('judge_class_started', {
        event_id: id,
        metadata: {
          class_id: classId,
          registration_id: next.id,
        },
      });
      navigate(scoreHref(next.id));
    }
  };

  const actionLabel = (r: JudgeRegistration) => {
    if (r.my_score_status === 'finalized') return 'View';
    if (r.my_score_status === 'draft') return 'Continue';
    return 'Start';
  };

  const groupedByClass = useMemo(() => {
    if (sort !== 'class') return null;
    const groups: { key: string; name: string; items: JudgeRegistration[] }[] = [];
    const map = new Map<string, { name: string; items: JudgeRegistration[] }>();
    for (const r of filtered) {
      const key = r.class_id != null ? String(r.class_id) : 'none';
      const name = r.class_name || 'Open class';
      if (!map.has(key)) {
        const g = { name, items: [] as JudgeRegistration[] };
        map.set(key, g);
        groups.push({ key, name, items: g.items });
      }
      map.get(key)!.items.push(r);
    }
    return groups;
  }, [filtered, sort]);

  if (loading) return <PageLoader message="Loading vehicles…" />;

  if (error && !dashboard) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-400 mb-4 text-center" role="alert">{error}</p>
        <PageBackLink label="Assigned events" to="/judge" className="mb-4 justify-center" />
      </div>
    );
  }

  const progress = dashboard?.progress;
  const eventTitle = dashboard?.event?.title || 'Event';
  const unfinishedExists = filtered.some((r) => r.my_score_status !== 'finalized');

  const renderCard = (r: JudgeRegistration) => {
    const photo = resolvePublicFileUrl(r.photo_url) || r.photo_url;
    return (
      <li key={r.id}>
        <Link
          to={scoreHref(r.id)}
          className="flex items-center gap-3 min-h-[72px] card px-3 py-3 hover:border-white/[0.12] active:bg-[#0c0c0c] transition-colors"
        >
          <div className="w-14 h-14 rounded-lg bg-[#141414] overflow-hidden shrink-0 border border-white/[0.08]">
            {photo ? (
              <img src={photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No photo</div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-white truncate">{vehicleLabel(r)}</p>
            <p className="text-sm text-gray-500 truncate">
              {r.class_name || 'Open class'} · {r.owner_display_name || `@${r.owner_username}`}
            </p>
            <span
              className={`inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_BADGE[r.my_score_status]}`}
            >
              {STATUS_LABELS[r.my_score_status]}
            </span>
          </div>
          <span className="text-sm text-gray-400 shrink-0 font-medium">{actionLabel(r)} →</span>
        </Link>
      </li>
    );
  };

  return (
    <div className="max-w-lg">
        <PageBackLink label="Assigned events" to="/judge" className="mb-5" />

        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Score vehicles</p>
        <h1 className="heading-display tracking-tight mb-1">{eventTitle}</h1>
        {dashboard?.event && (dashboard.event.city || dashboard.event.location) && (
          <p className="text-sm text-gray-500 mb-4">
            {dashboard.event.city && dashboard.event.state
              ? `${dashboard.event.city}, ${dashboard.event.state}`
              : dashboard.event.location}
          </p>
        )}

        {progress && (
          <div className="form-section mb-5">
            <div className="flex items-end justify-between gap-3 mb-2">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Your progress</p>
                <p className="stat-numeral mt-0.5">{progress.percent}%</p>
              </div>
              <p className="text-sm text-gray-400 text-right">
                {progress.finalized}/{progress.total} finalized
              </p>
            </div>
            <div className="h-2 rounded-full bg-[#141414] overflow-hidden mb-3">
              <div
                className="h-full bg-emerald-500/80 transition-all"
                style={{ width: `${Math.min(100, progress.percent)}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              <span>{progress.drafts} draft{progress.drafts === 1 ? '' : 's'}</span>
              <span>·</span>
              <span>{progress.not_started} not started</span>
              <span>·</span>
              <span>{progress.remaining} remaining</span>
            </div>
          </div>
        )}

        {unfinishedExists && (
          <Button
            variant="primary"
            fullWidth
            className="mb-5 min-h-[48px]"
            onClick={continueJudging}
          >
            Continue judging
          </Button>
        )}

        <div className="space-y-3 mb-5">
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setClassId(null)}
              className={classId === null ? 'filter-chip-active shrink-0' : 'filter-chip-inactive shrink-0'}
            >
              All classes
            </button>
            {(dashboard?.classes || []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setClassId(c.id)}
                className={classId === c.id ? 'filter-chip-active shrink-0' : 'filter-chip-inactive shrink-0'}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {(['all', 'not_started', 'draft', 'finalized'] as JudgeStatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={status === s ? 'filter-chip-active' : 'filter-chip-inactive'}
              >
                {s === 'all' ? 'All status' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vehicle, owner, class…"
            className="input"
          />

          <div className="flex items-center gap-2">
            <label htmlFor="judge-sort" className="input-label mb-0 shrink-0">
              Sort
            </label>
            <select
              id="judge-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as JudgeSortMode)}
              className="input flex-1"
            >
              <option value="class">By class</option>
              <option value="name">By name</option>
              <option value="status">By status</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-gray-500 text-sm leading-relaxed">
            {dashboard?.registrations.length === 0
              ? 'No vehicles assigned yet. Ask the organizer to add you to this event or assign your classes.'
              : 'No vehicles match these filters.'}
          </p>
        ) : groupedByClass ? (
          <div className="space-y-6">
            {groupedByClass.map((g) => (
              <section key={g.key}>
                <h2 className="eyebrow-muted mb-2">
                  {g.name}
                  <span className="text-gray-600 font-normal ml-2">({g.items.length})</span>
                </h2>
                <ul className="space-y-2">{g.items.map(renderCard)}</ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">{filtered.map(renderCard)}</ul>
        )}
    </div>
  );
};

export default JudgeEventVehicles;
